import { useCallback, useEffect, useState } from "react";
import api from "./api";

/**
 * Per-admin column preferences, persisted to MongoDB via /api/admin/me/prefs.
 *
 * Returns [value, setValue] where value = { order: string[], hidden: string[] }.
 * Saves are debounced to 500ms so rapid ▲/▼ clicks don't hammer the API.
 */
export default function useColumnPrefs(pageKey, defaults = { order: [], hidden: [] }) {
  const [loaded, setLoaded] = useState(false);
  const [value, setValue] = useState(defaults);

  useEffect(() => {
    let mounted = true;
    api.get("/admin/me/prefs").then((r) => {
      if (!mounted) return;
      const prefs = r.data || {};
      const page = prefs[pageKey];
      if (page && typeof page === "object") {
        setValue({
          order: Array.isArray(page.order) ? page.order : defaults.order,
          hidden: Array.isArray(page.hidden) ? page.hidden : defaults.hidden,
        });
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);

  // Persist with a short debounce so rapid edits coalesce.
  useEffect(() => {
    if (!loaded) return;
    const handle = setTimeout(async () => {
      try {
        const current = (await api.get("/admin/me/prefs")).data || {};
        const merged = { ...current, [pageKey]: value };
        await api.put("/admin/me/prefs", merged);
      } catch (_) { /* non-fatal — prefs will just reload next session */ }
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, loaded, pageKey]);

  const update = useCallback((next) => setValue((v) => ({ ...v, ...next })), []);
  return [value, update, loaded];
}
